/* TJC — 10 Years scroll story
   Progressive enhancement. The Liquid output is the finished page; this file
   adds the scroll animation on top when ALL of these are true:
     - GSAP + ScrollTrigger loaded
     - the visitor has not asked for reduced motion
   Otherwise it only lazy-plays video loops, and the static page stands.
   Animation touches transform, opacity and clip-path only. */
(() => {
  'use strict';

  const SEL = '[data-tjc10]';
  const DESKTOP = '(min-width: 990px)';
  const SCREENS_PER_SECOND = 0.45; // scroll length: screens of scrolling per second of timeline (raise = slower story)
  const INTRO_LEAD = 0.25; // screens of scrolling from the top of the page until the first year starts rolling (raise = longer intro)
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  /* ───────────────────────── Video loops ───────────────────────── */
  function initVideos(root) {
    const videos = root.querySelectorAll('video.tjc10-video');
    if (!videos.length) return () => {};
    videos.forEach((v) => {
      v.muted = true; // iOS needs the property, not only the attribute
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.removeAttribute('controls');
    });
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return () => {};
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target: v, isIntersecting }) => {
        if (isIntersecting) {
          if (v.preload === 'none') v.preload = 'auto';
          const p = v.play();
          if (p && p.catch) p.catch(() => {}); // autoplay refused: poster stays
        } else {
          v.pause();
        }
      });
    }, { rootMargin: '200px 0px' });
    videos.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }

  /* ───────────────────────── Sticky header offset ───────────────────────── */
  function headerOffset() {
    const h = document.querySelector('[data-sticky-type]');
    if (!h || h.getAttribute('data-sticky-type') !== 'always') return 0;
    return Math.round(h.getBoundingClientRect().height);
  }

  /* ───────────────────────── Rolling digits (odometer) ───────────────────────── */
  function column(a, b, up, fromNothing) {
    // a, b are single chars: a digit or ' ' (blank). Returns the cells to roll through.
    if (a === b) return [a];
    if (b === ' ') return [a, ' '];
    // A new leading digit (9 -> 10) drops straight in rather than rolling 0,1...
    if (a === ' ' && !fromNothing) return [' ', b];
    const target = +b;
    const out = [a];
    let d = a === ' ' ? (up ? -1 : 10) : +a;
    for (let guard = 0; guard < 11 && d !== target; guard++) {
      d = up ? (d + 1) % 10 : (d + 9) % 10;
      out.push(String(d));
    }
    return out;
  }

  function makeOdometer(el) {
    if (!el) return null;
    const toS = (el.dataset.to || '').trim();
    const fromS = (el.dataset.from || '').trim();
    const T = parseInt(toS, 10);
    if (Number.isNaN(T)) return null;
    const F = fromS === '' ? null : parseInt(fromS, 10);
    if (el.dataset.orig === undefined) el.dataset.orig = el.textContent;

    const tStr = String(T);
    const fStr = F === null ? '' : String(F);
    const len = Math.max(tStr.length, fStr.length);
    const tp = tStr.padStart(len, ' ');
    const fp = fStr.padStart(len, ' ');
    const up = F === null || T >= F;

    const wrap = document.createElement('span');
    wrap.className = 'tjc10-odo';
    wrap.setAttribute('aria-hidden', 'true');
    const cols = [];
    for (let i = 0; i < len; i++) {
      const seq = column(fp[i], tp[i], up, F === null);
      const col = document.createElement('span');
      col.className = 'tjc10-odo__col';
      seq.forEach((c) => {
        const cell = document.createElement('span');
        cell.textContent = c === ' ' ? ' ' : c;
        col.appendChild(cell);
      });
      wrap.appendChild(col);
      // Columns that appear (blank -> digit) or disappear (digit -> blank) also grow/shrink in
      // width, so a single digit stays centred and slides left as the second digit arrives.
      const grow = seq[0] === ' ' && seq[seq.length - 1] !== ' ';
      const shrink = seq[0] !== ' ' && seq[seq.length - 1] === ' ';
      cols.push({ col, steps: seq.length - 1, grow, shrink });
    }
    const sr = document.createElement('span');
    sr.className = 'tjc10-sr';
    sr.textContent = tStr;
    el.textContent = '';
    el.append(wrap, sr);
    el.classList.add('is-odo');
    // Natural width of each column in em (so it scales with the font).
    const fs = parseFloat(getComputedStyle(wrap).fontSize) || 16;
    cols.forEach((c) => { c.w = c.col.getBoundingClientRect().width / fs; });

    return {
      addTo(tl, at, duration) {
        cols.forEach(({ col, steps, grow, shrink, w }) => {
          if (!steps) return;
          tl.fromTo(col, { yPercent: 0 }, {
            yPercent: (-100 * steps) / (steps + 1),
            duration,
            ease: 'power2.inOut',
            immediateRender: true,
          }, at);
          if (grow) tl.fromTo(col, { width: '0em' }, { width: `${w}em`, duration, ease: 'power2.inOut', immediateRender: true }, at);
          if (shrink) tl.fromTo(col, { width: `${w}em` }, { width: '0em', duration, ease: 'power2.inOut', immediateRender: true }, at);
        });
      },
      restore() {
        el.textContent = el.dataset.orig;
        el.classList.remove('is-odo');
      },
    };
  }

  /* ───────────────────────── Line-by-line text ───────────────────────── */
  function splitWords(container) {
    if (!container) return [];
    if (!container.dataset.split) {
      const walk = (node) => {
        Array.from(node.childNodes).forEach((n) => {
          if (n.nodeType === 3) {
            const frag = document.createDocumentFragment();
            n.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) {
                frag.appendChild(document.createTextNode(part));
              } else {
                const w = document.createElement('span');
                w.className = 'tjc10-w';
                w.textContent = part;
                frag.appendChild(w);
              }
            });
            n.replaceWith(frag);
          } else if (n.nodeType === 1) {
            walk(n);
          }
        });
      };
      walk(container);
      container.dataset.split = '1';
    }
    return Array.from(container.querySelectorAll('.tjc10-w'));
  }

  function groupLines(words) {
    // offsetTop is layout-only (ignores transforms), so leftover tween offsets can't skew the grouping.
    const lines = [];
    let top = null;
    words.forEach((w) => {
      const t = w.offsetTop;
      if (top === null || Math.abs(t - top) > 4) {
        lines.push([]);
        top = t;
      }
      lines[lines.length - 1].push(w);
    });
    return lines;
  }

  /* ───────────────────────── Pile / spread positions for photos ───────────────────────── */
  const PILE_MOBILE = [
    { xPercent: -12, yPercent: -16 },
    { xPercent: 12, yPercent: 2 },
    { xPercent: -6, yPercent: 18 },
    { xPercent: 10, yPercent: -6 },
  ];
  const SPREAD_DESKTOP = [
    { xPercent: -46, yPercent: -10 },
    { xPercent: 22, yPercent: -2 },
    { xPercent: -10, yPercent: 20 },
    { xPercent: 50, yPercent: 16 },
  ];
  const SPREAD_DESKTOP_WIDE = [
    { xPercent: -31, yPercent: -26 },
    { xPercent: 30, yPercent: 24 },
    { xPercent: -6, yPercent: 40 },
    { xPercent: 30, yPercent: -30 },
  ];

  /* ───────────────────────── One milestone ───────────────────────── */
  function buildMilestone(gsap, ScrollTrigger, ms, api) {
    const desktop = window.matchMedia(DESKTOP).matches;
    const stage = ms.querySelector('[data-stage]');
    const yearBox = ms.querySelector('.tjc10-year');
    const burst = ms.querySelector('.tjc10-year__burst');
    const rings = Array.from(ms.querySelectorAll('.tjc10-rings span')).slice(0, 2); // red, black (the 3rd is unused)
    const content = ms.querySelector('.tjc10-ms__content');
    const story = ms.querySelector('.tjc10-story');
    const headline = ms.querySelector('.tjc10-story__headline');
    const subline = ms.querySelector('.tjc10-story__subline');
    const body = ms.querySelector('.tjc10-story__body');
    const photosLayer = ms.querySelector('[data-photos]');
    const photos = Array.from(ms.querySelectorAll('[data-photo]'));
    const cutouts = Array.from(ms.querySelectorAll('.tjc10-cutout'));
    const mapwrap = ms.querySelector('[data-map-moment]');

    const yearOdo = api.odo(ms.querySelector('.tjc10-year__num'));
    const lines = groupLines(splitWords(body));
    const stageH = () => stage.clientHeight;
    const vw = () => window.innerWidth;

    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
    let t = 0;

    /* 1. Year rolls up + red burst */
    if (yearOdo) yearOdo.addTo(tl, 0, 1);
    tl.fromTo(burst, { scale: 0, opacity: 0.95 }, { scale: 2.6, opacity: 0, duration: 0.9, ease: 'power1.out' }, 0.6);
    t = 1.3;

    /* 2. Concentric circles: solid red, then solid black, then the innermost "clear" circle,
       which is the content layer itself (paper-coloured), so it opens onto the story.
       All three share one ease and duration and start in turn, so they stay nested, and
       each grows just past the stage corners so the paper circle ends up covering the rest. */
    const RING_GAP = 0.16;
    const RING_DUR = 1;
    const coverR = () => Math.ceil(Math.hypot(stage.clientWidth, stage.clientHeight) / 2) + 4;
    rings.forEach((ring, i) => {
      tl.fromTo(ring, { scale: 0 }, {
        scale: () => coverR() / Math.max(1, ring.offsetWidth / 2),
        duration: RING_DUR,
        ease: 'power1.in',
        immediateRender: true,
      }, t + i * RING_GAP);
    });
    tl.fromTo(content,
      { clipPath: 'circle(0px at 50% 50%)', webkitClipPath: 'circle(0px at 50% 50%)' },
      {
        clipPath: () => `circle(${coverR()}px at 50% 50%)`,
        webkitClipPath: () => `circle(${coverR()}px at 50% 50%)`,
        duration: RING_DUR,
        ease: 'power1.in',
        immediateRender: true,
      },
      t + rings.length * RING_GAP);
    tl.to(yearBox, { opacity: 0, scale: 0.86, duration: 0.6, ease: 'power1.in' }, t + 0.45);
    t += 1.2;

    /* 3. Story: headline, small line, body line by line */
    const storyStart = t;
    if (headline) tl.from(headline, { y: 40, opacity: 0, duration: 0.45 }, t);
    if (subline) tl.from(subline, { y: 20, opacity: 0, duration: 0.35 }, t + 0.15);
    t += 0.45;
    const LINE = 0.14;
    // One tween per line (no stagger: staggered from() tweens don't all render their start state when nested).
    lines.forEach((ln, i) => {
      tl.fromTo(ln, { y: 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, immediateRender: true }, t + i * LINE);
    });
    const bodyDur = lines.length * LINE + 0.35;
    // Long entries on small phones: glide the story up as it reveals so nothing is clipped.
    tl.fromTo(story, { y: 0 }, {
      y: () => -Math.max(0, story.scrollHeight - story.clientHeight),
      duration: Math.max(bodyDur, 0.5),
      ease: 'none',
    }, t);
    t += bodyDur + 0.35;

    /* 4. Photos slide in and settle (desktop: alongside the story) */
    const pile = desktop ? SPREAD_DESKTOP : PILE_MOBILE;
    photos.forEach((p, i) => {
      const r = parseFloat(getComputedStyle(p).getPropertyValue('--r')) || 0;
      // Landscape prints are wider, so on desktop they get their own spread: clear of the
      // story text, and far enough apart that the top print doesn't hide the one under it.
      const spot = desktop && p.classList.contains('tjc10-photo--wide') ? SPREAD_DESKTOP_WIDE : pile;
      const pos = photos.length === 1 ? { xPercent: 0, yPercent: 0 } : spot[i % spot.length];
      gsap.set(p, { ...pos, rotation: r });
    });
    let photosStart = t;
    if (!desktop && photos.length) {
      tl.to(story, { opacity: 0, y: '-=50', duration: 0.4, ease: 'power1.in' }, t);
      photosStart = t + 0.2;
    } else if (desktop) {
      photosStart = storyStart + 0.3;
    }
    const PHOTO_GAP = 0.35;
    photos.forEach((p, i) => {
      const side = i % 2 ? 1 : -1;
      tl.from(p, {
        x: () => side * vw() * 1.25,
        y: () => stageH() * 0.25,
        rotation: `+=${side * 24}`,
        duration: 0.75,
        ease: 'power3.out',
      }, photosStart + i * PHOTO_GAP);
    });
    const photosEnd = photos.length ? photosStart + (photos.length - 1) * PHOTO_GAP + 0.75 + 0.5 : photosStart;
    if (desktop && photos.length) {
      // gentle depth drift while settled
      tl.to(photosLayer, { y: -30, duration: Math.max(photosEnd, t) - photosStart, ease: 'none' }, photosStart);
    }

    /* Cut-outs drift past at their own speed */
    cutouts.forEach((c, i) => {
      const dir = i % 2 ? 1 : -1;
      const amp = desktop ? 0.85 : 0.65;
      tl.fromTo(c,
        { y: () => stageH() * amp, x: desktop ? dir * 40 : 0, rotation: dir * -18 },
        { y: () => -stageH() * amp, x: desktop ? dir * -60 : 0, rotation: dir * 22, ease: 'none', duration: Math.max(photosEnd - photosStart, 1) + 1 },
        photosStart - 0.3 + i * 0.25);
    });
    t = Math.max(t, photosEnd);

    /* 5. Map moment */
    if (mapwrap) {
      const out = desktop ? [story, photosLayer] : [photosLayer, story];
      tl.to(out.filter(Boolean), { opacity: 0, scale: 0.94, duration: 0.45, ease: 'power1.in' }, t);
      tl.from(mapwrap, { opacity: 0, scale: 0.94, duration: 0.5 }, t + 0.3);
      const at = t + 0.75;
      mapwrap.querySelectorAll('[data-pin]').forEach((pin) => {
        const to = parseInt(pin.dataset.to, 10) || 0;
        const state = pin.dataset.pin;
        // Pop in the first time a pin is SHOWN, even if its count went above 0 at a map-off milestone.
        const wasShown = (api.lastShown[state] || 0) > 0;
        api.lastShown[state] = to;
        if (!wasShown && to > 0) {
          tl.from(pin.querySelectorAll('.tjc10-pin__badge, .tjc10-pin__label'), {
            scale: 0, opacity: 0, duration: 0.4, ease: 'back.out(2.4)',
          }, at);
          const extra = mapwrap.querySelectorAll(`[data-leader="${state}"], [data-city="${state}"]`);
          if (extra.length) tl.from(extra, { opacity: 0, duration: 0.3 }, at);
        }
        const o = api.odo(pin.querySelector('[data-roll]'));
        if (o) o.addTo(tl, at + 0.1, 0.9);
      });
      const tot = api.odo(mapwrap.querySelector('.tjc10-total [data-roll]'));
      if (tot) tot.addTo(tl, at + 0.1, 0.9);
      t = at + 1.2;

      /* 6. Outro on the SAME map (last milestone only): zoom out, pins light up, closing words */
      const words = ms.querySelector('[data-outro-words]');
      if (words) {
        const title = words.querySelector('.tjc10-outro__title');
        const text = words.querySelector('.tjc10-outro__text');
        const titleWords = splitWords(title);
        // Work out the final layout so map + total + closing words sit together as one
        // centred group, whatever the screen height (no big gap on tall screens).
        const mapEl = mapwrap.querySelector('.tjc10-map');
        const totEl = mapwrap.querySelector('.tjc10-total');
        const SH = stageH();
        const top0 = mapEl.offsetTop;
        const blockH = (totEl ? totEl.offsetTop + totEl.offsetHeight : mapEl.offsetTop + mapEl.offsetHeight) - top0;
        const wordsH = words.offsetHeight;
        const gap = desktop ? 28 : 18;
        const pad = 16;
        const sc = Math.max(0.5, Math.min(desktop ? 0.85 : 0.9, (SH - 2 * pad - gap - wordsH) / blockH));
        const groupTop = Math.max(pad, (SH - (sc * blockH + gap + wordsH)) / 2);
        gsap.set(words, { y: groupTop + sc * blockH + gap });
        tl.to(mapwrap, {
          scale: sc,
          y: groupTop - sc * top0,
          transformOrigin: '50% 0%',
          duration: 1.1,
          ease: 'power1.inOut',
        }, t);
        const lit = Array.from(mapwrap.querySelectorAll('.tjc10-pin:not(.is-empty) .tjc10-pin__badge'));
        lit.forEach((b, i) => {
          const at2 = t + 0.3 + i * 0.22;
          tl.to(b, { scale: 1.3, duration: 0.18, ease: 'power2.out' }, at2)
            .to(b, { scale: 1, duration: 0.25, ease: 'back.out(3)' }, at2 + 0.18);
        });
        const wordsAt = t + 0.9;
        titleWords.forEach((w, i) => {
          tl.fromTo(w, { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.55, immediateRender: true }, wordsAt + i * 0.12);
        });
        if (text) tl.fromTo(text, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, immediateRender: true }, wordsAt + titleWords.length * 0.12 + 0.2);
        t = wordsAt + titleWords.length * 0.12 + 0.9;
      }
    }

    tl.to({}, { duration: 0.5 }, t); // short hold before the stage scrolls away

    ms.style.setProperty('--units', (1 + tl.duration() * SCREENS_PER_SECOND).toFixed(2));

    // Load this milestone's images a little early: slid-out photos would otherwise wait on lazy-load.
    ScrollTrigger.create({
      trigger: ms,
      start: () => `top bottom+=${Math.round(window.innerHeight * 1.5)}`,
      once: true,
      onEnter: () => ms.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }),
    });

    ScrollTrigger.create({
      trigger: ms,
      start: () => `top ${api.top()}px`,
      end: () => `bottom ${api.top() + stageH()}px`,
      scrub: 0.6,
      animation: tl,
      invalidateOnRefresh: true,
    });
  }

  /* ───────────────────────── Outro ───────────────────────── */
  function buildOutro(gsap, ScrollTrigger, outro, api) {
    const stage = outro.querySelector('[data-stage]');
    const map = outro.querySelector('.tjc10-map');
    const pinBits = Array.from(outro.querySelectorAll('.tjc10-pin:not(.is-empty)'))
      .map((p) => p.querySelectorAll('.tjc10-pin__badge, .tjc10-pin__label'));
    const title = outro.querySelector('.tjc10-outro__title');
    const text = outro.querySelector('.tjc10-outro__text');
    const titleWords = splitWords(title);

    const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
    if (map) tl.fromTo(map, { scale: 1.25 }, { scale: 0.86, duration: 1.8, ease: 'power1.inOut' }, 0);
    pinBits.forEach((bits, i) => {
      tl.fromTo(bits, { opacity: 0.2, scale: 0.7 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(2.4)' }, 0.2 + i * 0.18);
    });
    const tot = api.odo(outro.querySelector('.tjc10-total [data-roll]'));
    if (tot) tot.addTo(tl, 0.2, 1.3);
    titleWords.forEach((w, i) => {
      tl.fromTo(w, { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.55, immediateRender: true }, 1.3 + i * 0.12);
    });
    if (text) tl.from(text, { y: 20, opacity: 0, duration: 0.5 }, 1.3 + titleWords.length * 0.12 + 0.2);
    tl.to({}, { duration: 0.6 });

    outro.style.setProperty('--units', (1 + tl.duration() * SCREENS_PER_SECOND).toFixed(2));
    ScrollTrigger.create({
      trigger: outro,
      start: () => `top ${api.top()}px`,
      end: () => `bottom ${api.top() + stage.clientHeight}px`,
      scrub: 0.6,
      animation: tl,
      invalidateOnRefresh: true,
    });
  }

  /* ───────────────────────── Intro ───────────────────────── */
  function buildIntro(gsap, intro, firstMs, api) {
    if (!intro) return;
    // Tuck the first milestone up under the intro so its year starts rolling after a short
    // scroll, rather than after a whole screen of empty paper. Its stage is see-through
    // until the year rolls in, so the overlap is invisible.
    const stageH = () => window.innerHeight - api.top();
    if (firstMs) {
      const lead = Math.round(stageH() * INTRO_LEAD);
      intro.style.marginBottom = `${-Math.max(0, intro.offsetHeight - lead)}px`;
    }
    gsap.to(intro.children, {
      y: -60,
      opacity: 0,
      stagger: 0.05,
      ease: 'none',
      scrollTrigger: {
        trigger: intro,
        start: () => `top ${api.top()}px`,
        // Fully faded by the time the first year starts rolling.
        endTrigger: firstMs || intro,
        end: () => (firstMs ? `top ${api.top()}px` : 'bottom top'),
        scrub: 0.4,
      },
    });
  }

  /* ───────────────────────── Per-section setup ───────────────────────── */
  function setup(root) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const stopVideos = initVideos(root);
    if (!gsap || !ScrollTrigger) return { destroy: stopVideos };

    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    let top = headerOffset();
    const odos = [];
    const api = {
      top: () => top,
      odo: (el) => {
        const o = makeOdometer(el);
        if (o) odos.push(o);
        return o;
      },
    };

    let ctx = null;
    const build = () => {
      api.lastShown = {};
      ctx = gsap.context(() => {
        root.classList.add('is-motion');
        root.style.setProperty('--tjc10-top', `${top}px`);
        buildIntro(gsap, root.querySelector('[data-scene="intro"]'), root.querySelector('[data-scene="milestone"]'), api);
        root.querySelectorAll('[data-scene="milestone"]').forEach((ms) => buildMilestone(gsap, ScrollTrigger, ms, api));
        const outro = root.querySelector('[data-scene="outro"]');
        if (outro) buildOutro(gsap, ScrollTrigger, outro, api);
      }, root);
      ScrollTrigger.refresh();
    };
    const teardown = () => {
      if (ctx) ctx.revert();
      ctx = null;
      odos.splice(0).forEach((o) => o.restore());
      root.classList.remove('is-motion');
      root.querySelectorAll('[data-scene]').forEach((s) => s.style.removeProperty('--units'));
      const intro = root.querySelector('[data-scene="intro"]');
      if (intro) intro.style.removeProperty('margin-bottom');
    };

    const apply = () => {
      teardown();
      if (!reduceMotion.matches) build();
    };

    // Rebuild on width change only (address-bar height changes are ignored).
    let lastW = window.innerWidth;
    let timer = null;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const newTop = headerOffset();
        if (window.innerWidth !== lastW || newTop !== top) {
          lastW = window.innerWidth;
          top = newTop;
          apply();
        }
      }, 250);
    };
    window.addEventListener('resize', onResize);
    const onMotionPref = () => apply();
    if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onMotionPref);

    // Wait for the theme fonts so line breaks are measured correctly.
    const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    ready.then(apply, apply);

    return {
      destroy() {
        window.removeEventListener('resize', onResize);
        if (reduceMotion.removeEventListener) reduceMotion.removeEventListener('change', onMotionPref);
        teardown();
        stopVideos();
      },
    };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SEL).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, setup(root));
    });
  }

  function destroyIn(scope) {
    instances.forEach((inst, root) => {
      if (scope.contains(root)) {
        inst.destroy();
        instances.delete(root);
      }
    });
  }

  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:unload', (e) => destroyIn(e.target));
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();
